import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, LogOut, Image as ImageIcon, Check, X, Key, GripVertical, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'outdoor', name: 'Outdoor Amenities' },
  { id: 'bedrooms', name: 'Bedrooms' },
  { id: 'living', name: 'Living Spaces' },
  { id: 'kitchen', name: 'Kitchen & Dining' },
  { id: 'bathrooms', name: 'Bathrooms' },
  { id: 'game', name: 'Game Room' }
];

// Sortable Image Card Component
const SortableImageCard = ({ image, onDelete, token }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        background: 'white',
        borderRadius: 'var(--border-radius-lg)',
        overflow: 'hidden',
        boxShadow: isDragging ? '0 12px 35px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.1)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
      }}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${image.id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.5rem',
          background: 'var(--color-coastal-fog)',
          cursor: 'grab',
          borderBottom: '1px solid var(--color-harbor-mist)'
        }}
      >
        <GripVertical size={20} color="var(--color-granite-gray)" />
        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--color-granite-gray)' }}>
          Drag to reorder
        </span>
      </div>
      
      <div style={{ position: 'relative', paddingTop: '66.67%' }}>
        <img
          src={image.url.startsWith('http') ? image.url : `${BACKEND_URL}${image.url}`}
          alt={image.alt_text}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>
      <div style={{ padding: 'var(--spacing-md)' }}>
        <p style={{
          fontSize: '0.875rem',
          color: 'var(--color-granite-gray)',
          marginBottom: 'var(--spacing-sm)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {image.alt_text}
        </p>
        <button
          data-testid={`delete-image-${image.id}`}
          onClick={() => onDelete(image.id, image.filename)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.625rem',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: 'var(--border-radius)',
            color: '#c33',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#fcc';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#fee';
          }}
        >
          <Trash2 size={16} />
          Delete
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [images, setImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('outdoor');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchImages();
  }, [token, navigate]);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/auth/change-password`,
        {
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [selectedCategory]);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setUploading(true);
    setUploadProgress('Uploading...');

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', selectedCategory);
      formData.append('alt_text', file.name.replace(/\.[^/.]+$/, ""));
      formData.append('order', images.filter(img => img.category === selectedCategory).length + i);

      try {
        await axios.post(`${BACKEND_URL}/api/admin/images/upload`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setUploadProgress(`Uploaded ${i + 1}/${files.length}`);
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadProgress(`Error uploading ${file.name}`);
      }
    }

    await fetchImages();
    setUploading(false);
    setUploadProgress('Upload complete!');
    setTimeout(() => setUploadProgress(''), 3000);
  };

  const handleDelete = async (imageId, filename) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchImages();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete image');
    }
  };

  const handleImportPhotos = async () => {
    if (!window.confirm('Import all default website photos into the admin? This will not create duplicates.')) return;
    setImporting(true);
    setImportMessage('');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/images/seed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImportMessage(response.data.message);
      await fetchImages();
    } catch (error) {
      console.error('Import failed:', error);
      setImportMessage('Failed to import photos');
    } finally {
      setImporting(false);
      setTimeout(() => setImportMessage(''), 5000);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setReordering(true);
      
      const oldIndex = filteredImages.findIndex((img) => img.id === active.id);
      const newIndex = filteredImages.findIndex((img) => img.id === over.id);
      
      const newFilteredOrder = arrayMove(filteredImages, oldIndex, newIndex);
      
      // Update local state immediately for smooth UX
      const newImages = [...images];
      newFilteredOrder.forEach((img, index) => {
        const imgIndex = newImages.findIndex(i => i.id === img.id);
        if (imgIndex !== -1) {
          newImages[imgIndex] = { ...newImages[imgIndex], order: index };
        }
      });
      setImages(newImages);

      // Send reorder request to backend
      try {
        const reorderData = newFilteredOrder.map((img, index) => ({
          id: img.id,
          order: index
        }));
        
        await axios.post(
          `${BACKEND_URL}/api/admin/images/reorder`,
          reorderData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } catch (error) {
        console.error('Reorder failed:', error);
        // Refetch on error to sync with server
        await fetchImages();
      } finally {
        setReordering(false);
      }
    }
  };

  const filteredImages = images
    .filter(img => img.category === selectedCategory)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-coastal-fog)' }} data-testid="admin-dashboard">
      {/* Header */}
      <div style={{
        background: 'var(--color-ocean-deep)',
        color: 'white',
        padding: 'var(--spacing-lg)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div className="container-coastal" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--spacing-md)'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '2rem',
              marginBottom: '0.25rem'
            }}>
              Photo Management
            </h1>
            <p style={{ opacity: 0.9 }}>Upload, organize, and reorder your property photos</p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
            <button
              data-testid="import-photos-btn"
              onClick={handleImportPhotos}
              disabled={importing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: importing ? 'rgba(255,255,255,0.1)' : 'var(--color-sunset-gold)',
                border: '2px solid var(--color-sunset-gold)',
                borderRadius: 'var(--border-radius)',
                color: 'white',
                cursor: importing ? 'wait' : 'pointer',
                fontWeight: '600',
                transition: 'background 0.3s ease'
              }}
            >
              {importing ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
              {importing ? 'Importing...' : 'Import Website Photos'}
            </button>
            <button
              data-testid="change-password-btn"
              onClick={() => setShowPasswordModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid white',
                borderRadius: 'var(--border-radius)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'background 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              <Key size={20} />
              Change Password
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid white',
                borderRadius: 'var(--border-radius)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'background 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="container-coastal" style={{ padding: 'var(--spacing-2xl) var(--spacing-lg)' }}>
        {/* Import Message */}
        {importMessage && (
          <div data-testid="import-message" style={{
            padding: 'var(--spacing-md)',
            background: importMessage.includes('Failed') ? '#fee' : '#d4edda',
            border: `1px solid ${importMessage.includes('Failed') ? '#fcc' : '#c3e6cb'}`,
            borderRadius: 'var(--border-radius)',
            color: importMessage.includes('Failed') ? '#c33' : '#155724',
            marginBottom: 'var(--spacing-lg)',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Check size={18} />
            {importMessage}
          </div>
        )}

        {/* Category Tabs */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-sm)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              data-testid={`category-tab-${cat.id}`}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: '0.75rem 1.5rem',
                background: selectedCategory === cat.id ? 'var(--color-ocean-deep)' : 'white',
                color: selectedCategory === cat.id ? 'white' : 'var(--color-granite-gray)',
                border: `2px solid ${selectedCategory === cat.id ? 'var(--color-ocean-deep)' : 'var(--color-coastal-fog)'}`,
                borderRadius: 'var(--border-radius)',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {cat.name} ({images.filter(img => img.category === cat.id).length})
            </button>
          ))}
        </div>

        {/* Upload Area */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          data-testid="upload-dropzone"
          style={{
            border: `3px dashed ${dragActive ? 'var(--color-ocean-deep)' : 'var(--color-harbor-mist)'}`,
            borderRadius: 'var(--border-radius-lg)',
            padding: 'var(--spacing-2xl)',
            background: dragActive ? 'rgba(10, 61, 98, 0.05)' : 'white',
            textAlign: 'center',
            marginBottom: 'var(--spacing-xl)',
            transition: 'all 0.3s ease'
          }}
        >
          <Upload size={48} color="var(--color-ocean-deep)" style={{ margin: '0 auto var(--spacing-md) auto' }} />
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            marginBottom: 'var(--spacing-sm)',
            color: 'var(--color-dark-slate)'
          }}>
            {dragActive ? 'Drop images here' : 'Drag & drop images'}
          </h3>
          <p style={{ color: 'var(--color-granite-gray)', marginBottom: 'var(--spacing-md)' }}>
            or click to browse
          </p>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleChange}
            style={{ display: 'none' }}
            id="fileInput"
            data-testid="file-input"
          />
          <label
            htmlFor="fileInput"
            data-testid="choose-files-btn"
            style={{
              display: 'inline-block',
              padding: '0.875rem 2rem',
              background: 'var(--color-ocean-deep)',
              color: 'white',
              borderRadius: 'var(--border-radius)',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'background 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = 'var(--color-ocean-blue)'}
            onMouseLeave={(e) => e.target.style.background = 'var(--color-ocean-deep)'}
          >
            Choose Files
          </label>
          <p style={{ marginTop: 'var(--spacing-md)', fontSize: '0.875rem', color: 'var(--color-harbor-mist)' }}>
            Images will be uploaded to: <strong>{categories.find(c => c.id === selectedCategory)?.name}</strong>
          </p>
          {uploadProgress && (
            <div style={{
              marginTop: 'var(--spacing-md)',
              padding: 'var(--spacing-sm)',
              background: uploading ? 'var(--color-coastal-fog)' : '#d4edda',
              borderRadius: 'var(--border-radius)',
              color: uploading ? 'var(--color-granite-gray)' : '#155724',
              fontWeight: '600'
            }}>
              {uploadProgress}
            </div>
          )}
        </div>

        {/* Images Grid with Drag and Drop */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)'
          }}>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              color: 'var(--color-dark-slate)'
            }}>
              {categories.find(c => c.id === selectedCategory)?.name} ({filteredImages.length} images)
            </h3>
            {filteredImages.length > 1 && (
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--color-ocean-deep)',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <GripVertical size={16} />
                Drag images to reorder
                {reordering && <span style={{ marginLeft: '0.5rem', opacity: 0.7 }}>Saving...</span>}
              </p>
            )}
          </div>
          
          {filteredImages.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 'var(--spacing-2xl)',
              background: 'white',
              borderRadius: 'var(--border-radius-lg)'
            }}>
              <ImageIcon size={64} color="var(--color-harbor-mist)" style={{ margin: '0 auto var(--spacing-md) auto' }} />
              <p style={{ color: 'var(--color-granite-gray)', fontSize: '1.125rem' }}>
                No images in this category yet. Upload some above!
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredImages.map(img => img.id)}
                strategy={rectSortingStrategy}
              >
                <div
                  data-testid="images-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: 'var(--spacing-md)'
                  }}
                >
                  {filteredImages.map((image) => (
                    <SortableImageCard
                      key={image.id}
                      image={image}
                      onDelete={handleDelete}
                      token={token}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'var(--spacing-lg)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: 'var(--border-radius-lg)',
            padding: 'var(--spacing-2xl)',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 'var(--spacing-lg)'
            }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.75rem',
                color: 'var(--color-dark-slate)'
              }}>
                Change Password
              </h2>
              <button
                data-testid="close-password-modal"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordError('');
                  setPasswordSuccess('');
                  setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} color="var(--color-granite-gray)" />
              </button>
            </div>

            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'var(--color-dark-slate)'
                }}>
                  Current Password
                </label>
                <input
                  type="password"
                  data-testid="current-password-input"
                  value={passwordData.oldPassword}
                  onChange={(e) => setPasswordData({...passwordData, oldPassword: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid var(--color-coastal-fog)',
                    borderRadius: 'var(--border-radius)',
                    fontSize: '1rem',
                    fontFamily: 'var(--font-body)'
                  }}
                />
              </div>

              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'var(--color-dark-slate)'
                }}>
                  New Password (min. 6 characters)
                </label>
                <input
                  type="password"
                  data-testid="new-password-input"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid var(--color-coastal-fog)',
                    borderRadius: 'var(--border-radius)',
                    fontSize: '1rem',
                    fontFamily: 'var(--font-body)'
                  }}
                />
              </div>

              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'var(--color-dark-slate)'
                }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  data-testid="confirm-password-input"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid var(--color-coastal-fog)',
                    borderRadius: 'var(--border-radius)',
                    fontSize: '1rem',
                    fontFamily: 'var(--font-body)'
                  }}
                />
              </div>

              {passwordError && (
                <div data-testid="password-error" style={{
                  padding: 'var(--spacing-md)',
                  background: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: 'var(--border-radius)',
                  color: '#c33',
                  marginBottom: 'var(--spacing-md)',
                  fontSize: '0.9375rem'
                }}>
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div data-testid="password-success" style={{
                  padding: 'var(--spacing-md)',
                  background: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: 'var(--border-radius)',
                  color: '#155724',
                  marginBottom: 'var(--spacing-md)',
                  fontSize: '0.9375rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Check size={18} />
                  {passwordSuccess}
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                <button
                  type="button"
                  data-testid="cancel-password-btn"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--color-coastal-fog)',
                    color: 'var(--color-granite-gray)',
                    border: 'none',
                    borderRadius: 'var(--border-radius)',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-password-btn"
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--color-ocean-deep)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--border-radius)',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)'
                  }}
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
